// StudentPathways.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";

/** ----------------------------------------------------------------
 * StudentPathways
 *
 * Props:
 *   - platformUrl: Learn base URL (e.g., https://your.learn.edu)
 *   - backEndUrl: backend base URL for the pathways service
 *
 * URL query params:
 *   - uuid:    current user's Learn UUID
 *   - token:   Learn REST token (Bearer)
 *
 * Backend (student-facing):
 *   - GET {backEndUrl}/pathways?userId={userPk1}
 *     → { items: [ { PathwayId, PathwayLabel, Requirements, Enrollments?, UpdatedAt? } ] }
 *
 *     Requirements: [
 *       {
 *         Order,
 *         Title,
 *         MinRequired,
 *         Courses: [{ CoursePk1, EnrollUrl?, Credits? }]
 *       }
 *     ]
 *
 * Learn:
 *   - GET /learn/api/public/v1/users/uuid:{uuid}
 *   - GET /learn/api/public/v1/users/uuid:{uuid}/courses?expand=course
 *   - GET /learn/api/public/v3/courses/{pk1}
 *   - GET /learn/api/public/v2/courses/{pk1}/gradebook/columns
 *   - GET /learn/api/public/v2/courses/{pk1}/gradebook/columns/{columnId}/users/{userPk1}
 * ---------------------------------------------------------------- */

/** ---------------- i18n-ish messages (student flavor) ---------------- */
const MESSAGES = {
    en_US: {
        headings: {
            main: "My Pathway",
            requirements: "Requirements",
        },
        userBanner: (name) => `Pathway for ${name || "Student"}`,
        notices: {
            loading: "Loading your pathway…",
            noPathways: "No pathways have been assigned to you.",
            failedLoad: "Failed to load pathway.",
        },
        table: {
            requirement: "Requirement",
            name: "Course Name",
            id: "Course ID",
            availability: "Availability",
            status: "Status",
            credits: "Credits",
            earnedCredits: "Earned",
            courseLink: "Course",
            enroll: "Enroll",
            noCourses: "No courses in this requirement yet.",
        },
        status: {
            inProgress: "In Progress",
            open: "Open",
            completed: "Completed",
        },
        buttons: {
            openCourse: "Open course",
            enroll: "Enroll",
            downloadTranscript: "Download transcript (PDF)",
        },
        summary: {
            creditsCompletedLabel: "Credits Completed",
            creditsExplanation:
                "Required credits are based on the minimum number of courses you must complete in each requirement.",
        },
    },
    en_AU: {},
};

const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Learn API adapter ---------- */
function makeLearnApi(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const v1 = "/learn/api/public/v1";
    const v2 = "/learn/api/public/v2";
    const v3 = "/learn/api/public/v3";

    async function getUserByUuid(uuid) {
        const { data } = await http.get(`${v1}/users/uuid:${uuid}`);
        return data;
    }

    async function listUserCoursesByUuid(uuid) {
        // memberships for this user (expand=course to get course.id pk1)
        const results = [];
        const firstResp = await http.get(`${v1}/users/uuid:${uuid}/courses`, {
            params: { expand: "course" },
        });
        const body = firstResp.data || {};
        const first = Array.isArray(body) ? body : body.results || [];
        results.push(...first);

        let next = body?.paging?.nextPage || null;
        while (next) {
            const { data: more } = await http.get(next);
            const chunk = Array.isArray(more) ? more : more.results || [];
            results.push(...chunk);
            next = more?.paging?.nextPage || null;
        }
        return results;
    }

    async function getCourseByPk1(pk1) {
        const { data } = await http.get(`${v3}/courses/${pk1}`);
        return data;
    }

    // list gradebook columns for a course
    async function listCourseGradeColumns(coursePk1) {
        const { data } = await http.get(
            `${v2}/courses/${coursePk1}/gradebook/columns`
        );
        return data;
    }

    // get the user's grade for a given column
    async function getUserGradeForColumn(coursePk1, columnId, userPk1) {
        const { data } = await http.get(
            `${v2}/courses/${coursePk1}/gradebook/columns/${columnId}/users/${userPk1}`
        );
        return data;
    }

    return {
        getUserByUuid,
        listUserCoursesByUuid,
        getCourseByPk1,
        listCourseGradeColumns,
        getUserGradeForColumn,
    };
}

/** ---------- Backend API adapter ---------- */
function makeBackendApi(backEndUrl) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Accept: "application/json" },
    });

    return {
        // Assumes backend supports filtering by userId (pk1)
        listStudentPathways: async (userPk1) => {
            const { data } = await http.get(`/pathways`, {
                params: { userId: userPk1 },
            });
            if (Array.isArray(data?.items)) return data.items;
            if (Array.isArray(data)) return data;
            return [];
        },
    };
}

/** ---------- Utilities ---------- */
function normalizeCourseFromRequirement(c) {
    if (!c) return null;
    const id = c.CoursePk1 || c.coursePk1 || c.id || c.pk1;
    if (!id) return null;
    const creditsRaw = c.Credits ?? c.credits;
    const credits = Number.isFinite(Number(creditsRaw))
        ? Number(creditsRaw)
        : 0;

    return {
        id: String(id), // pk1
        credits,
        // The admin tool stores this as EnrollUrl on the backend
        enrollUrl: c.EnrollUrl || c.enrollUrl || "",
    };
}

function normalizePathwayItem(item) {
    if (!item) return null;
    const pathwayId =
        item.PathwayId ||
        item.pathwayId ||
        item.Id ||
        item.id ||
        null;

    const label =
        item.PathwayLabel ||
        item.pathwayLabel ||
        item.Label ||
        item.label ||
        "";

    const rawReqs = item.Requirements || item.requirements || [];
    const steps = (rawReqs || []).map((r, idx) => {
        const rawCourses = r.Courses || r.courses || [];
        const courses = rawCourses
            .map((c) => normalizeCourseFromRequirement(c))
            .filter(Boolean);
        return {
            id: `step_${idx + 1}`,
            order: Number(r.Order || r.order || idx + 1),
            title: r.Title || r.title || "",
            minRequired: Number(r.MinRequired || r.minRequired || 1) || 1,
            courses,
        };
    });

    const updatedAt = item.UpdatedAt || item.updatedAt || null;

    return {
        pathwayId: pathwayId ? String(pathwayId) : null,
        label,
        steps,
        updatedAt,
    };
}

/**
 * Hydrate pathway courses with full Learn course details:
 *   - name
 *   - courseId
 *   - availability
 *   - externalAccessUrl
 *
 * Keeps `enrollUrl` and `credits` from the backend.
 */
async function hydratePathwaysFromLearn(pathways, learnApi) {
    if (!Array.isArray(pathways) || pathways.length === 0) return pathways;

    // collect unique course pk1s
    const courseIdSet = new Set();
    for (const p of pathways) {
        (p.steps || []).forEach((step) => {
            (step.courses || []).forEach((c) => {
                if (c?.id) courseIdSet.add(String(c.id));
            });
        });
    }
    const courseIds = Array.from(courseIdSet);

    try {
        const courses = await Promise.all(
            courseIds.map(async (id) => {
                try {
                    const data = await learnApi.getCourseByPk1(id);
                    return { ok: true, id, data };
                } catch (err) {
                    console.warn("[StudentPathways] Failed to hydrate course", id, err);
                    return { ok: false, id, data: null };
                }
            })
        );

        const courseMap = new Map();
        for (const c of courses) {
            if (!c.ok || !c.data) continue;
            const d = c.data;
            const pk1 = d.pk1 ?? d.id ?? c.id;
            if (!pk1) continue;

            courseMap.set(String(pk1), {
                id: String(pk1),
                courseId: d.courseId || d.externalId || "",
                name: d.name || d.title || "(untitled course)",
                availability:
                    d.availability?.available || d.availability?.availability || "",
                externalAccessUrl: d.externalAccessUrl || "",
            });
        }

        const hydrated = pathways.map((p) => ({
            ...p,
            steps: (p.steps || []).map((step) => ({
                ...step,
                courses: (step.courses || []).map((c) => {
                    const full = courseMap.get(String(c.id));
                    const credits = Number.isFinite(Number(c.credits))
                        ? Number(c.credits)
                        : 0;

                    if (!full) {
                        // keep id + enrollUrl + credits if no Learn data
                        return {
                            id: String(c.id),
                            courseId: "",
                            name: "",
                            availability: "",
                            externalAccessUrl: "",
                            enrollUrl: c.enrollUrl || "",
                            credits,
                        };
                    }
                    return {
                        id: full.id,
                        courseId: full.courseId,
                        name: full.name,
                        availability: full.availability,
                        externalAccessUrl: full.externalAccessUrl,
                        enrollUrl: c.enrollUrl || "",
                        credits,
                    };
                }),
            })),
        }));

        return hydrated;
    } catch (e) {
        console.warn("[StudentPathways] Hydration failed, using raw pathways", e);
        return pathways;
    }
}

/**
 * For all courses in the pathways, look up a grade column named "Completion".
 * If the user's grade text is "Complete" (or "Completed"), record that fact.
 *
 * Returns a plain object: { [coursePk1]: "Completed" }
 */
async function buildCompletionStatus(pathways, learnApi, userPk1) {
    const completion = {};

    if (!Array.isArray(pathways) || pathways.length === 0 || !userPk1) {
        return completion;
    }

    const courseIdSet = new Set();
    for (const p of pathways) {
        (p.steps || []).forEach((step) => {
            (step.courses || []).forEach((c) => {
                if (c?.id) courseIdSet.add(String(c.id));
            });
        });
    }
    const courseIds = Array.from(courseIdSet);

    await Promise.all(
        courseIds.map(async (coursePk1) => {
            try {
                const colsData = await learnApi.listCourseGradeColumns(coursePk1);

                // data might be { results: [...] } or [...] – be defensive
                const cols = Array.isArray(colsData)
                    ? colsData
                    : Array.isArray(colsData?.results)
                        ? colsData.results
                        : [];

                const completionCol = cols.find((col) => {
                    const name =
                        (col.name ||
                            col.title ||
                            col.columnName ||
                            "").toString().trim().toLowerCase();
                    return name === "completion";
                });

                if (!completionCol || !completionCol.id) {
                    return;
                }

                const gradeData = await learnApi.getUserGradeForColumn(
                    coursePk1,
                    completionCol.id,
                    userPk1
                );

                // gradeData may be a single object or { results: [...] }
                const record = Array.isArray(gradeData?.results)
                    ? gradeData.results[0]
                    : gradeData;

                if (!record) return;

                // Prefer displayGrade.text when displayGrade is an object
                let rawValue = "";

                if (record.displayGrade) {
                    if (
                        typeof record.displayGrade === "object" &&
                        record.displayGrade !== null
                    ) {
                        rawValue =
                            record.displayGrade.text ??
                            record.displayGrade.displayGrade ??
                            "";
                    } else {
                        rawValue = record.displayGrade.toString();
                    }
                } else if (
                    record.score &&
                    typeof record.score.displayGrade !== "undefined"
                ) {
                    rawValue = String(record.score.displayGrade);
                } else if (record.score && typeof record.score.text !== "undefined") {
                    rawValue = String(record.score.text);
                } else if (typeof record.grade !== "undefined") {
                    rawValue = String(record.grade);
                } else if (typeof record.text !== "undefined") {
                    rawValue = String(record.text);
                }

                const normalized = rawValue.trim().toLowerCase();

                // Treat both "complete" and "completed" as completed
                if (normalized === "complete" || normalized === "completed") {
                    completion[String(coursePk1)] = "Completed";
                }
            } catch (err) {
                console.warn(
                    "[StudentPathways] Failed to get completion grade",
                    coursePk1,
                    err
                );
            }
        })
    );

    return completion;
}

/**
 * Compute total credits needed vs earned, taking minRequired into account.
 *
 * For each requirement:
 *   - let N = number of courses with credits
 *   - let M = minRequired (clamped to [1, N])
 *   - totalNeededForRequirement = (sum of credits) * (M / N)
 *     (average credits per course * M)
 *
 * Credits earned = sum of credits for courses where status is "Completed".
 */
function computeCreditsSummary(pathway, completionByCourseId) {
    const result = { totalCreditsNeeded: 0, earnedCredits: 0 };
    if (!pathway || !pathway.steps) return result;

    const completionMap = completionByCourseId || {};

    pathway.steps.forEach((step) => {
        const courses = step.courses || [];

        const courseCredits = courses
            .map((c) =>
                Number.isFinite(Number(c.credits)) ? Number(c.credits) : 0
            )
            .filter((v) => v > 0);

        const numCourses = courseCredits.length;
        if (numCourses > 0) {
            const rawMin =
                Number.isFinite(Number(step.minRequired)) &&
                Number(step.minRequired) > 0
                    ? Number(step.minRequired)
                    : 1;
            const minReq = Math.max(1, Math.min(rawMin, numCourses));
            const sumCredits = courseCredits.reduce((a, b) => a + b, 0);
            const neededForStep = (sumCredits * minReq) / numCourses;
            result.totalCreditsNeeded += neededForStep;
        }

        courses.forEach((c) => {
            const credits = Number.isFinite(Number(c.credits))
                ? Number(c.credits)
                : 0;
            if (credits <= 0) return;
            const coursePk1 = String(c.id);
            const isCompleted = completionMap[coursePk1] === "Completed";
            if (isCompleted) {
                result.earnedCredits += credits;
            }
        });
    });

    return result;
}

/** ---------- Compact UI CSS ---------- */
const compactCss = `
  .bb-container {
    font-size: 13px;
    padding: 8px 10px;
    background: #f5f5f7;
  }

  .bb-card {
    background: #fff;
  }

  .bb-card h2 { font-size: 16px; font-weight: 700 !important; }

  .bb-table { font-size: 12px; border-collapse: collapse; width: 100%; table-layout: fixed; }
  .bb-table th, .bb-table td { padding: 6px 8px; border-bottom: 1px solid #eee; }
  .bb-table th { font-weight: 600; text-align: left; }

  .bb-card .bb-button,
  .bb-container .bb-button {
    -webkit-appearance: none !important;
    appearance: none !important;
    background: #f6f6f6 !important;
    border: 1px solid #d3d3d3 !important;
    color: #222 !important;
    border-radius: 6px !important;
    padding: 6px 10px !important;
    font: inherit !important;
    line-height: 1.2 !important;
    text-shadow: none !important;
    box-shadow: none !important;
    cursor: pointer !important;
  }
  .bb-card .bb-button:hover,
  .bb-container .bb-button:hover { background: #efefef !important; }
  .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }

  .bb-row { display: flex; gap: 8px; align-items: center; }

  .status-pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    border: 1px solid transparent;
  }
  .status-inprogress {
    background: #e6f3ff;
    border-color: #a6c8ff;
    color: #114a8b;
  }
  .status-open {
    background: #fff7e6;
    border-color: #ffd699;
    color: #8a5300;
  }
  .status-completed {
    background: #e6ffed;
    border-color: #9ae6b4;
    color: #22543d;
  }
`;

/** ---------- Flash message ---------- */
const Flash = ({ type, children }) => {
    if (!children) return null;
    const styles = {
        marginBottom: 6,
        padding: "8px 10px",
        borderRadius: 6,
        border: "1px solid",
        background: type === "error" ? "#ffecec" : "#ecfff1",
        borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
        color: type === "error" ? "#a40000" : "#005c2e",
        fontSize: 12,
    };
    return (
        <div
            role={type === "error" ? "alert" : "status"}
            style={styles}
            aria-live="polite"
        >
            {children}
        </div>
    );
};

/** ---------- Component ---------- */
const StudentPathways = ({ platformUrl, backEndUrl, locale = "en_US" }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const uuid = queryParams.get("uuid") || "";
    const token = queryParams.get("token") || "";

    const learn = useMemo(
        () => makeLearnApi(platformUrl, token),
        [platformUrl, token]
    );
    const backend = useMemo(
        () => makeBackendApi(backEndUrl),
        [backEndUrl]
    );

    const [loading, setLoading] = useState(true);
    const [fatalError, setFatalError] = useState("");
    const [inlineError, setInlineError] = useState("");

    const [userDisplayName, setUserDisplayName] = useState("");
    const [pathways, setPathways] = useState([]); // student pathways
    const [enrolledCourseIds, setEnrolledCourseIds] = useState(new Set());
    // { [coursePk1]: "Completed" }
    const [completionByCourseId, setCompletionByCourseId] = useState({});

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!uuid || !token || !platformUrl || !backEndUrl) {
                setFatalError("Missing uuid, token, platformUrl, or backEndUrl.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setFatalError("");
            setInlineError("");

            try {
                // 1) Get user info
                const user = await learn.getUserByUuid(uuid);
                if (!cancelled && user) {
                    const nameObj = user.name || {};
                    const pieces = [];
                    if (nameObj.given) pieces.push(nameObj.given);
                    if (nameObj.middle) pieces.push(nameObj.middle);
                    if (nameObj.family) pieces.push(nameObj.family);
                    const display =
                        nameObj.displayName || pieces.join(" ") || user.userName || "";
                    setUserDisplayName(display);
                }

                const userPk1 = String((user && (user.pk1 ?? user.id)) || "");

                // 2) Get user course memberships, build set of enrolled course pk1s
                const memberships = await learn.listUserCoursesByUuid(uuid);
                const enrolledSet = new Set(
                    (memberships || [])
                        .map((m) => m.course?.id || m.courseId)
                        .filter(Boolean)
                        .map((id) => String(id))
                );
                if (!cancelled) {
                    setEnrolledCourseIds(enrolledSet);
                }

                // 3) Load student's pathways from backend (filtered by userPk1)
                const items = await backend.listStudentPathways(userPk1);
                const normalized = (items || [])
                    .map((it) => normalizePathwayItem(it))
                    .filter(Boolean);

                // 4) Hydrate courses from Learn (names, courseIds, externalAccessUrl)
                const hydrated = await hydratePathwaysFromLearn(normalized, learn);

                // 5) Build completion status map from gradebook
                let completionMap = {};
                try {
                    completionMap = await buildCompletionStatus(
                        hydrated,
                        learn,
                        userPk1
                    );
                } catch (e) {
                    console.warn(
                        "[StudentPathways] buildCompletionStatus failed",
                        e
                    );
                }

                if (!cancelled) {
                    setPathways(hydrated);
                    setCompletionByCourseId(completionMap);
                }
            } catch (err) {
                console.error("[StudentPathways] load error", err);
                if (!cancelled) {
                    setFatalError(
                        err?.response?.data?.message ||
                        err.message ||
                        M.notices.failedLoad
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [uuid, token, platformUrl, backEndUrl, learn, backend, M.notices.failedLoad]);

    const primaryPathway = pathways[0] || null;

    // Credits summary (needed vs earned), accounting for minRequired
    const { totalCreditsNeeded, earnedCredits } = useMemo(
        () => computeCreditsSummary(primaryPathway, completionByCourseId),
        [primaryPathway, completionByCourseId]
    );

    const handleDownloadTranscript = () => {
        setInlineError("");

        if (!primaryPathway) {
            setInlineError("No pathway available to download.");
            return;
        }

        const doc = new jsPDF();
        const leftMargin = 14;
        const topMargin = 16;
        const lineHeight = 6;
        let y = topMargin;

        doc.setFontSize(14);
        doc.text(M.headings.main, leftMargin, y);
        y += lineHeight;

        doc.setFontSize(11);
        if (userDisplayName) {
            doc.text(M.userBanner(userDisplayName), leftMargin, y);
            y += lineHeight;
        }

        doc.text(
            `Pathway: ${primaryPathway.label || primaryPathway.pathwayId || "-"}`,
            leftMargin,
            y
        );
        y += lineHeight;

        const summary = computeCreditsSummary(
            primaryPathway,
            completionByCourseId
        );
        if (summary.totalCreditsNeeded > 0) {
            const neededRounded =
                Math.round(summary.totalCreditsNeeded * 100) / 100;
            doc.text(
                `${M.summary.creditsCompletedLabel}: ${summary.earnedCredits} / ${neededRounded}`,
                leftMargin,
                y
            );
            y += lineHeight;

            doc.setFontSize(9);
            doc.text(M.summary.creditsExplanation, leftMargin, y);
            doc.setFontSize(11);
            y += lineHeight;
        }

        y += 2;

        (primaryPathway.steps || []).forEach((step, index) => {
            if (y > 280) {
                doc.addPage();
                y = topMargin;
            }

            doc.setFontSize(12);
            let title = `Requirement ${index + 1}`;
            if (step.title) title += `: ${step.title}`;
            doc.text(title, leftMargin, y);
            y += lineHeight;

            doc.setFontSize(10);
            doc.text(
                `Must complete at least ${step.minRequired} course(s) from this requirement.`,
                leftMargin,
                y
            );
            y += lineHeight;

            (step.courses || []).forEach((c) => {
                if (y > 280) {
                    doc.addPage();
                    y = topMargin;
                }

                const coursePk1 = String(c.id);
                const isEnrolled = enrolledCourseIds.has(coursePk1);
                const isCompleted =
                    completionByCourseId[coursePk1] === "Completed";

                let statusLabel;
                if (isCompleted) statusLabel = M.status.completed;
                else if (isEnrolled) statusLabel = M.status.inProgress;
                else statusLabel = M.status.open;

                const credits = Number.isFinite(Number(c.credits))
                    ? Number(c.credits)
                    : 0;
                const earnedForCourse = isCompleted ? credits : 0;

                let line = `- ${c.name || "-"} (${c.courseId || "-"}) | Status: ${statusLabel}`;
                if (credits > 0) {
                    line += ` | Credits: ${credits}`;
                    if (earnedForCourse > 0) {
                        line += ` | Earned: ${earnedForCourse}`;
                    }
                }

                doc.text(line, leftMargin, y);
                y += lineHeight;
            });

            y += lineHeight / 2;
        });

        doc.save("pathway-transcript.pdf");
    };

    if (loading) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.notices.loading}</div>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div
                    className="bb-card"
                    style={{
                        padding: 10,
                        border: "1px solid #ddd",
                        borderRadius: 8,
                    }}
                >
                    <h2 style={{ margin: 0 }}>Error</h2>
                    <p style={{ fontSize: 12 }}>{fatalError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            <div
                className="bb-card"
                style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                }}
            >
                <div
                    className="bb-row"
                    style={{ justifyContent: "space-between", marginBottom: 4 }}
                >
                    <div>
                        <h2 style={{ margin: 0 }}>{M.headings.main}</h2>
                        {userDisplayName && (
                            <p style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}>
                                {M.userBanner(userDisplayName)}
                            </p>
                        )}
                    </div>
                    {primaryPathway && (
                        <button
                            type="button"
                            className="bb-button"
                            onClick={handleDownloadTranscript}
                        >
                            {M.buttons.downloadTranscript}
                        </button>
                    )}
                </div>

                <Flash type="error">{inlineError}</Flash>

                {!primaryPathway ? (
                    <p style={{ fontSize: 12 }}>{M.notices.noPathways}</p>
                ) : (
                    <>
                        <p style={{ fontSize: 12, marginTop: 4 }}>
                            <strong>Pathway:</strong>{" "}
                            {primaryPathway.label || primaryPathway.pathwayId || "-"}
                        </p>

                        {totalCreditsNeeded > 0 && (
                            <>
                                <p style={{ fontSize: 12, marginTop: 2 }}>
                                    <strong>{M.summary.creditsCompletedLabel}:</strong>{" "}
                                    {earnedCredits} /{" "}
                                    {Math.round(totalCreditsNeeded * 100) / 100}
                                </p>
                                <p
                                    style={{
                                        fontSize: 11,
                                        marginTop: 2,
                                        color: "#555",
                                        maxWidth: 600,
                                    }}
                                >
                                    {M.summary.creditsExplanation}
                                </p>
                            </>
                        )}

                        {/* Requirements */}
                        <div
                            style={{
                                marginTop: 8,
                                padding: 8,
                                borderRadius: 6,
                                border: "1px solid #eee",
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: 14,
                                    fontWeight: 600,
                                }}
                            >
                                {M.headings.requirements}
                            </h3>

                            {!(primaryPathway.steps || []).length ? (
                                <p style={{ fontSize: 12, marginTop: 8 }}>
                                    {M.table.noCourses}
                                </p>
                            ) : (
                                <div
                                    style={{
                                        marginTop: 8,
                                        display: "grid",
                                        gap: 8,
                                    }}
                                >
                                    {(primaryPathway.steps || []).map((step, index) => (
                                        <div
                                            key={step.id}
                                            style={{
                                                borderRadius: 6,
                                                border: "1px solid #ddd",
                                                padding: 8,
                                                background: "#fafafa",
                                            }}
                                        >
                                            <div
                                                className="bb-row"
                                                style={{
                                                    justifyContent: "space-between",
                                                    marginBottom: 6,
                                                }}
                                            >
                                                <div style={{ fontSize: 12 }}>
                                                    <strong>
                                                        {`Requirement ${index + 1}`}
                                                    </strong>
                                                    {step.title
                                                        ? `: ${step.title}`
                                                        : ""}
                                                    <div style={{ marginTop: 2 }}>
                                                        Must complete at least{" "}
                                                        <strong>{step.minRequired}</strong>{" "}
                                                        course
                                                        {step.minRequired === 1 ? "" : "s"} from
                                                        this list.
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Unified table layout across requirements */}
                                            {step.courses && step.courses.length > 0 ? (
                                                <table className="bb-table" role="grid">
                                                    <colgroup>
                                                        <col style={{ width: "28%" }} />
                                                        <col style={{ width: "16%" }} />
                                                        <col style={{ width: "13%" }} />
                                                        <col style={{ width: "11%" }} />
                                                        <col style={{ width: "11%" }} />
                                                        <col style={{ width: "12%" }} />
                                                        <col style={{ width: "9%" }} />
                                                    </colgroup>
                                                    <thead>
                                                    <tr>
                                                        <th>{M.table.name}</th>
                                                        <th>{M.table.id}</th>
                                                        <th>{M.table.status}</th>
                                                        <th>{M.table.credits}</th>
                                                        <th>{M.table.earnedCredits}</th>
                                                        <th>{M.table.courseLink}</th>
                                                        <th>{M.table.enroll}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {step.courses.map((c) => {
                                                        const coursePk1 = String(c.id);
                                                        const isEnrolled =
                                                            enrolledCourseIds.has(
                                                                coursePk1
                                                            );
                                                        const isCompleted =
                                                            completionByCourseId[
                                                                coursePk1
                                                                ] === "Completed";

                                                        let statusLabel;
                                                        let statusClass;

                                                        if (isCompleted) {
                                                            statusLabel =
                                                                M.status.completed;
                                                            statusClass =
                                                                "status-completed";
                                                        } else if (isEnrolled) {
                                                            statusLabel =
                                                                M.status.inProgress;
                                                            statusClass =
                                                                "status-inprogress";
                                                        } else {
                                                            statusLabel = M.status.open;
                                                            statusClass = "status-open";
                                                        }

                                                        const credits = Number.isFinite(
                                                            Number(c.credits)
                                                        )
                                                            ? Number(c.credits)
                                                            : 0;
                                                        const earnedForCourse = isCompleted
                                                            ? credits
                                                            : 0;

                                                        return (
                                                            <tr key={c.id}>
                                                                <td>{c.name || "-"}</td>
                                                                <td>{c.courseId || "-"}</td>
                                                                <td>
                                                                    <span
                                                                        className={`status-pill ${statusClass}`}
                                                                    >
                                                                        {statusLabel}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {credits > 0
                                                                        ? credits
                                                                        : "-"}
                                                                </td>
                                                                <td>
                                                                    {earnedForCourse > 0
                                                                        ? earnedForCourse
                                                                        : "-"}
                                                                </td>
                                                                <td>
                                                                    {(isEnrolled ||
                                                                        isCompleted) &&
                                                                    c.externalAccessUrl ? (
                                                                        <a
                                                                            href={
                                                                                c.externalAccessUrl
                                                                            }
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            {
                                                                                M.buttons
                                                                                    .openCourse
                                                                            }
                                                                        </a>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {/* enroll link when not enrolled/completed and enrollUrl is present */}
                                                                    {!isEnrolled &&
                                                                    !isCompleted &&
                                                                    c.enrollUrl ? (
                                                                        <a
                                                                            href={c.enrollUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            {M.buttons.enroll}
                                                                        </a>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p
                                                    style={{
                                                        fontSize: 12,
                                                        margin: 0,
                                                    }}
                                                >
                                                    {M.table.noCourses}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default StudentPathways;